if (customElements.get('quick-view') === undefined) {

  customElements.define('quick-view', class QuickView extends HTMLElement {
    constructor() {
      'use strict';
      super();

      this.processingQuickViewRequest = false;
      this.button = this.querySelector('button');

      if (!this.button) {
        console.error('Quick view button not found');
        return;
      }

      this.quickViewTargetId = this.button.dataset.open;
      this.quickViewProductHandle = this.button.dataset.productHandle;

      // Extract product ID from modal/drawer ID (format: modal-{productId}-product or drawer-{productId}-product)
      const targetIdMatch = this.quickViewTargetId.match(/(?:modal|drawer)-(\d+)-product/);
      const productId = targetIdMatch ? targetIdMatch[1] : this.quickViewProductHandle;

      // Set missing dataset properties
      this.dataset.product = productId;
      this.dataset.url = this.button.dataset.url || window.location.origin + '/products/' + this.quickViewProductHandle;

      this.button.addEventListener('click', this.onQuickViewRequest.bind(this));
    }

    async onQuickViewRequest(event) {
      event.preventDefault();
      if (this.processingQuickViewRequest === true) {
        return;
      }

      this.button.classList.add('loading');

      // Check if the view already exists -> Open it
      const currentQuickView = document.getElementById(this.quickViewTargetId);
      if (currentQuickView) {
        Woolman.ModalsAndDrawers.showModalOrDrawer(this.quickViewTargetId);
        this.button.classList.remove('loading');
        return;
      }

      // If the view does not exist, fetch it
      this.processingQuickViewRequest = true;

      // Remove any existing quick views to prevent duplicates
      document.querySelectorAll('[data-quick-view]').forEach((quickView) => {
        quickView.remove();
      });

      try {
        const rootUrl = window.routes.root == '/' ? '/' : window.routes.root + '/';
        const res = await fetch(`${rootUrl}products/${this.quickViewProductHandle}?view=quick-view`);

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const quickViewElement = doc.body.firstChild;
        document.body.appendChild(quickViewElement);

        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        this.quickView = quickViewElement;
        this.hookEvents();

        setTimeout(() => {
          this.button.classList.remove('loading');
          Woolman.ModalsAndDrawers.showModalOrDrawer(this.quickViewTargetId);
        }, 100);
      } catch (error) {
        console.error('Error loading quick view:', error);
        this.button.classList.remove('loading');
        this.processingQuickViewRequest = false;
      }
    }

    hookEvents() {
      this.updateOptions();

      const radiosElement = this.quickView.querySelector('[data-quick-view-radios]');
      if (radiosElement) {
        // Store the bound function reference so we can remove it later
        this.boundVariantChangeHandler = this.onVariantChange.bind(this);
        radiosElement.addEventListener('change', this.boundVariantChangeHandler);
      }

      const closeButton = this.quickView.querySelector('[data-close]');
      if (closeButton) {
        closeButton.addEventListener('click', (e) => {
          Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent(e);
          setTimeout(() => this.cleanupQuickView(), 300);
        });
      }

      const overlay = this.quickView.querySelector('[data-overlay]');
      if (overlay) {
        overlay.addEventListener('click', (e) => {
          Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent(e);
          setTimeout(() => this.cleanupQuickView(), 300);
        });
      }

      const productForm = document.getElementById(`product-form-${this.dataset.product}`);
      if (productForm) {
        productForm.addEventListener('submit', this.handleSubmit.bind(this));
      }

      // Initialize quick-view-radios web component
      this.initializeQuickViewRadios();

      this.processingQuickViewRequest = false;
    }

    initializeQuickViewRadios() {
      const quickViewRadiosElement = this.quickView.querySelector('quick-view-radios');
      if (quickViewRadiosElement) {
        // Check if the element is already upgraded
        if (quickViewRadiosElement.constructor === HTMLElement) {
          // Force upgrade by cloning and replacing
          const upgradedElement = document.createElement('quick-view-radios');
          // Copy all attributes
          Array.from(quickViewRadiosElement.attributes).forEach(attr => {
            upgradedElement.setAttribute(attr.name, attr.value);
          });
          // Copy all child nodes
          while (quickViewRadiosElement.firstChild) {
            upgradedElement.appendChild(quickViewRadiosElement.firstChild);
          }
          // Replace the element
          quickViewRadiosElement.parentNode.replaceChild(upgradedElement, quickViewRadiosElement);
        }
      }
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        this.updateVariantInput();
        this.renderProductInfo();
      }
    }

    updateOptions() {
      const fieldsets = Array.from(this.quickView.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      });
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.product}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        if (this.quickView.dataset.isPreorder == 'true') {
          addButtonText.textContent = window.variantStrings.preOrder;
        } else {
          addButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      if (!modifyClass) return;
    }

    renderProductInfo() {
      fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&view=quick-buy-modal`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html')
          const destination = document.getElementById(`price-${this.dataset.product}`);
          const source = html.getElementById(`price-${this.dataset.product}`);

          if (source && destination) destination.innerHTML = source.innerHTML;

          const price = document.getElementById(`price-${this.dataset.product}`);

          if (price) price.classList.remove('hidden');
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);

          // Update product size info
          const sizeInfoDestination = this.quickView.querySelector('.product-size-info');
          const sizeInfoSource = html.querySelector('.product-size-info');
          if (sizeInfoSource && sizeInfoDestination) {
            sizeInfoDestination.innerHTML = sizeInfoSource.innerHTML;
          }
        })
        .catch((error) => {
          console.error('Error updating product info:', error);
        });
    }

    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;

      const mediaGridySlider = document.getElementById('slider-quick-view');

      if (mediaGridySlider) {
        mediaGridySlider.scrollToSlideByID(
          `item-${this.currentVariant.featured_media.id}`
        );
      }
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.product}, #product-form-installment-${this.dataset.product}`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    setUnavailable() {
      const button = document.getElementById(`product-form-${this.dataset.product}`);
      const addButton = button.querySelector('[name="add"]');
      const addButtonText = button.querySelector('[name="add"] > span');
      const price = document.getElementById(`price-${this.dataset.product}`);
      if (!addButton) return;
      addButtonText.textContent = window.variantStrings.unavailable;
      if (price) price.classList.add('hidden');
    }

    getVariantData() {
      if (!this.variantData) {
        const jsonElement = this.quickView.querySelector('[type="application/json"]');
        if (jsonElement) {
          this.variantData = JSON.parse(jsonElement.textContent);
        } else {
          console.error('Variant data JSON element not found');
          this.variantData = [];
        }
      }
      return this.variantData;
    }

    async handleSubmit() {
      const overlay = this.quickView.querySelector('[data-overlay]');
      if (overlay) {
        overlay.click();
      }
      Woolman.ModalsAndDrawers.closeModalOrDrawer(this.quickViewTargetId);

      // Clean up the quick view after a short delay to allow the modal/drawer to close
      setTimeout(() => {
        this.cleanupQuickView();
      }, 300);
    }

    cleanupQuickView() {
      if (this.quickView && this.quickView.parentNode) {
        // Remove event listeners before removing the element
        const radiosElement = this.quickView.querySelector('[data-quick-view-radios]');
        if (radiosElement && this.boundVariantChangeHandler) {
          radiosElement.removeEventListener('change', this.boundVariantChangeHandler);
        }

        const closeButton = this.quickView.querySelector('[data-close]');
        if (closeButton) {
          closeButton.removeEventListener('click', Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent);
        }

        const overlay = this.quickView.querySelector('[data-overlay]');
        if (overlay) {
          overlay.removeEventListener('click', Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent);
        }

        const productForm = document.getElementById(`product-form-${this.dataset.product}`);
        if (productForm) {
          productForm.removeEventListener('submit', this.handleSubmit.bind(this));
        }

        this.quickView.remove();
        this.quickView = null;
      }
    }
  });

}

if (customElements.get('quick-add') === undefined) {

  customElements.define('quick-add', class QuickAdd extends HTMLElement {
    constructor() {
      'use strict';
      super();

      this.button = this.querySelector('button');
      this.variantId = this.dataset.variantId;
      this.productId = this.dataset.productId;

      this.button.addEventListener('click', this.quickAddToCart.bind(this));
    }

    async quickAddToCart(event) {
      event.preventDefault();

      if (!this.variantId) {
        console.warn('No variant ID found on the add to cart button');
        return;
      }

      const rootUrl = window.routes.root == '/' ? '/' : window.routes.root + '/';
      const formData = {
        'items': [{
          'id': this.variantId,
          'quantity': 1
        }]
      };

      this.button.setAttribute('disabled', true);
      this.button.classList.add('loading');

      try {
        const response = await fetch(rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const data = await response.json();
          this.showTooltip(data.message);
          throw new Error(`Network response was not ok: ${data.message}`);
        } else {
          const event = new CustomEvent('ajaxProduct:added');
          document.dispatchEvent(event);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      }
    }

    showTooltip(message) {
      let tooltip = this.querySelector('.tooltip');
      if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.className = 'tooltip size--tiny';
        this.appendChild(tooltip);
      }
      tooltip.textContent = message;
      tooltip.style.display = 'block';
      setTimeout(() => {
        tooltip.style.display = 'none';
      }, 3000);
    }
  });

}

if (customElements.get('quick-view-radios') === undefined) {

  customElements.define('quick-view-radios', class QuickViewRadios extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Ensure the element is properly initialized when connected to DOM
      if (!this.hasAttribute('data-initialized')) {
        this.setAttribute('data-initialized', 'true');
        this.addEventListener('change', this.onVariantChange.bind(this));
      }
    }

    onVariantChange(e) {
      this.updateOptions();
      this.updateMasterId();
      this.updateVariantStatuses();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        const checkedRadio = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
        return checkedRadio ? checkedRadio.value : null;
      });

      this.updateLegends();
    }

    updateLegends() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((fieldset) => {
        const legend = fieldset.querySelector('legend [data-variant-legend]');
        const selectedOption = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
        if (legend && selectedOption) {
          legend.textContent = selectedOption.getAttribute('value');
        }
      });
    }

    updateMasterId() {
      // Only proceed if all options are selected
      if (!this.options || this.options.some(option => option === null)) {
        this.currentVariant = null;
        return;
      }

      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    getVariantData() {
      if (!this.variantData) {
        const jsonElement = this.querySelector('[type="application/json"]');
        if (jsonElement) {
          this.variantData = JSON.parse(jsonElement.textContent);
        } else {
          console.error('Variant data JSON element not found in QuickViewRadios');
          this.variantData = [];
        }
      }
      return this.variantData;
    }

    updateVariantStatuses() {
      if (!this.variantData || this.variantData.length === 0) {
        return;
      }

      const firstOption = this.querySelector(':checked');
      const selectedOptionOneVariants = this.variantData.filter(variant => {
        return firstOption && variant.option1 === firstOption.value;
      });

      const fieldsets = [...this.querySelectorAll('fieldset')];

      fieldsets.forEach((fieldset, index) => {
        if (index === 0) {
          return;
        }

        const optionInputs = [...fieldset.querySelectorAll('input[type="radio"]')];
        const previousFieldset = fieldsets[index - 1];
        const previousOptionSelected = previousFieldset.querySelector(':checked');

        if (previousOptionSelected) {
          const availableOptionInputsValue = selectedOptionOneVariants
            .filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected.value)
            .map(variantOption => variantOption[`option${index + 1}`]);

          this.setInputAvailability(optionInputs, availableOptionInputsValue);
        } else {
          console.log(`No previousOptionSelected for fieldset ${index}`);
        }
      });
    }

    setInputAvailability(listOfOptions, listOfAvailableOptions) {
      listOfOptions.forEach(input => {
        const inputValue = input.getAttribute('value');
        if (listOfAvailableOptions.includes(inputValue)) {
          input.classList.remove('disabled');
          input.disabled = false;
        } else {
          input.classList.add('disabled');
          input.disabled = true;
        }
      });
    }
  });

}
